// A company's beneficial owners, tax identification number, W-9 and insurance certificate are kept
// out of the listing anyone can read. The company's own screens load them separately and lay them
// over the listing they hold, where those screens already look for them.
//
// The W-9 and insurance keys are read from the map the API server uses, each time they are needed,
// so the two can never spell them differently.

import { companyIdCredentialKeys, insuranceKeys } from './companyDocumentKeys';

export const withCompanyCredentialDetails = (listing, details) => {
  if (!listing || !details) return listing;

  const publicData = listing.attributes?.publicData || {};
  const insurance = Object.fromEntries(
    insuranceKeys().filter(key => details[key] !== undefined).map(key => [key, details[key]])
  );

  return {
    ...listing,
    attributes: {
      ...listing.attributes,
      publicData: {
        ...publicData,
        ...insurance,
        beneficialOwners: details.beneficialOwners || [],
        companyIdentification: {
          ...(publicData.companyIdentification || {}),
          ...(details.companyIdentification || {}),
        },
      },
    },
  };
};

const omitKeys = (object, keys) =>
  Object.fromEntries(Object.entries(object).filter(([key]) => !keys.includes(key)));

// The listing data a profile save sends, when the company's details may not have been loaded into
// the form. Without them the form holds blanks, and saving those would erase the stored values, so
// owners and insurance are left out and the tax ID and W-9 stay as the listing already had them.
// A save that creates the listing has nothing stored to erase, so it sends everything as entered.
export const withLoadedCompanyCredentials = (
  publicData,
  { credentialsLoaded, createsListing, loadedPublicData } = {}
) => {
  if (credentialsLoaded || createsListing || !publicData) return publicData;

  const rest = omitKeys(publicData, ['beneficialOwners', ...insuranceKeys()]);
  if (!publicData.companyIdentification) return rest;

  const loadedId = loadedPublicData?.companyIdentification || {};
  const heldId = Object.fromEntries(
    companyIdCredentialKeys().filter(key => loadedId[key] !== undefined).map(key => [key, loadedId[key]])
  );

  return {
    ...rest,
    companyIdentification: {
      ...omitKeys(publicData.companyIdentification, companyIdCredentialKeys()),
      ...heldId,
    },
  };
};

// A user profile's public data without the company's credential values: those belong to the
// company's listing, and anything on a user profile can be read by anyone.
export const withoutCompanyCredentials = profilePublicData => {
  if (!profilePublicData) return profilePublicData;

  const rest = omitKeys(profilePublicData, ['beneficialOwners', 'insuranceCertificate', ...insuranceKeys()]);
  if (!profilePublicData.companyIdentification) return rest;

  return {
    ...rest,
    companyIdentification: omitKeys(profilePublicData.companyIdentification, companyIdCredentialKeys()),
  };
};
